/**
 * Service Layer - RTC Service (Main Orchestrator)
 * Coordinates all other services and handles call lifecycle
 * Main entry point for RTC operations
 */

import {
    messageDataTypeEnum,
    messageTypeEnum as roomChatMessageTypeEnum,
    RECORDING_STOP_THRESHOLD,
    SocketManager
} from '../core';
import {MediaService} from './MediaService';
import {DrawingService} from './DrawingService';
import {StreamService} from './StreamService';
import {TranscriptionService} from './TranscriptionService';
import {ParticipantService} from './ParticipantService';
import {RtcRequester} from '../../../MobxStores/RequestApi/RtcRequester';
import {messageTypeEnum, HEARTBEAT_INTERVAL, PING_INTERVAL} from '../core';
import {deviceTypeEnum} from '../../../serverClientShared/sharedEnums';
import {v4 as uuidv4} from "uuid";
import {RecordingService} from "./RecordingService";
import {generalStore} from "../../../index";


export class RtcService {
    constructor(store) {
        this.store = store;

        // Initialize core socket manager
        this.socketManager = new SocketManager();

        // Initialize services
        this.mediaService = new MediaService(store, this.socketManager);
        this.streamService = new StreamService(store, this.socketManager);
        this.drawingService = new DrawingService(store, this.socketManager);
        this.transcriptionService = new TranscriptionService(store, this.socketManager);
        this.participantService = new ParticipantService(store, this.socketManager, this.streamService);
        this.recordingService = new RecordingService(store, this.socketManager);

        // Intervals
        this.heartbeatInterval = null;
        this.pingInterval = null;
        this.playAudioInterval = null;
    }

    /**
     * Join a call
     */
    async joinCall(roomId, password) {
        try {
            // Get room details
            const {room} = await RtcRequester.getRoom(roomId, password);

            this.recordingService.checkExistingRecording(room)

            this.store.transcriptionToken = room.TranscriptionToken;
            this.store.transcriptionRegion = room.TranscriptionRegion;
            this.store.transcriptionLanguages = JSON.parse(room.TranscriptionLanguages || '{}');
            this.store.room = room;
            // Connect to socket server
            const serverAddress = room.RoomServerId.replace('http', 'ws').replace('/socket.io', '');
            this.socketManager.connect(serverAddress, room.RoomToken);

            // Join rooms
            this.socketManager.joinRoom(roomId);
            this.socketManager.joinRoom(this.store.deviceId);

            // Setup listeners
            this.setupSocketListeners();

            // Start intervals
            this.startHeartbeat();
            this.startPingInterval();
            this.startPlayAudioInterval();

            // Start activity monitoring
            this.participantService.startActivityMonitoring();

            // Broadcast mic state after a delay
            setTimeout(() => {
                this.participantService.broadcastMicState();
            }, 1000);

            return room;
        } catch (error) {
            console.error('Error joining call:', error);
            throw error;
        }
    }

    /**
     * Leave the call
     */
    async leaveCall() {
        // Stop all intervals
        this.stopHeartbeat();
        this.stopPingInterval();
        this.stopPlayAudioInterval();

        // Stop transcription
        await this.transcriptionService.cleanup();

        // Stop streaming
        this.streamService.cleanup();

        // Cleanup media
        this.mediaService.cleanupAllTracks();

        // Stop activity monitoring
        this.participantService.cleanup();

        // Notify other participants
        this.socketManager.notifyUserLeft(this.store.roomId, this.store.deviceId, this.socketManager.disconnect);

        // Close session
        await this.closeSession();
    }

    sendMessage(messageText) {
        const trimmedMessage = messageText.trim();
        if (!trimmedMessage) {
            return;
        }

        const chatMessage = JSON.stringify({
            Sender: {
                id: generalStore.userDetails?._id || uuidv4(),
                Name: this.store.name,
                LastName: generalStore.userDetails?.LastName
            },
            MessageText: trimmedMessage,
            UTCTimestamp: new Date().toISOString(),
            MessageDataType: messageDataTypeEnum.TEXT,
            messageType: roomChatMessageTypeEnum.TEXT,
            MessageId: messageTypeEnum.CHAT_MESSAGE
        });
        this.store.sendRoomMessage(chatMessage, this.store.roomId);

        const newMessage = {
            name: this.store.name,
            body: trimmedMessage,
            time: new Date(),
            fromSelf: true
        }
        this.store.messages.push(newMessage);
    }

    /**
     * Setup socket event listeners
     */
    setupSocketListeners() {
        // Heartbeat
        this.socketManager.on("OnHeartbeat", (data) => {
            this.participantService.handleHeartbeat(data);
        });

        // User left
        this.socketManager.on("OnUserLeft", (data) => {
            this.participantService.handleParticipantLeft(data);
        });

        // Video stream
        this.socketManager.on("GetTexture", (vp8Buffer) => {
            this.streamService.handleReceivedVideoData(vp8Buffer);
        });

        // Audio stream
        this.socketManager.on("OnDataMessage", (audioBuffer) => {
            this.streamService.handleReceivedAudioData(audioBuffer);
        });

        // Room messages
        this.socketManager.on("OnRoomMessage", (stringifiedData) => {
            this.handleRoomMessage(stringifiedData);
        });

        // Screen share started
        this.socketManager.on("OnShareScreenStarted", (data) => {
            this.handleShareScreenStarted(data);
        });

        // Keyframe request
        this.socketManager.on("RequestKeyframe", () => {
            this.store.isNextFrameKey = true;
        });
    }

    /**
     * Handle room messages
     */
    handleRoomMessage(stringifiedData) {
        const roomMessage = JSON.parse(stringifiedData);

        if (roomMessage.SenderSocketId === this.store.deviceId) {
            return;
        }

        const sender = this.store.participantsMap.get(roomMessage.SenderSocketId);
        const isSenderWeb = sender ? sender.isWeb : false;
        const messageData = JSON.parse(roomMessage.data);

        // Update mic state from message
        if (messageData.isMuted !== undefined && messageData.SessionID !== this.store.deviceId) {
            this.store.participantsMicStates.set(messageData.SessionID, !messageData.isMuted);
        }

        switch (messageData.MessageId) {
            case messageTypeEnum.TRANSCRIPTION_MESSAGE:
                this.transcriptionService.addTranscriptionMessage(messageData);
                break;

            case messageTypeEnum.TRANSCRIPTION_REQUEST:
                this.transcriptionService.handleTranscriptionRequest();
                break;

            case messageTypeEnum.CHAT_MESSAGE:
                this.handleChatMessage(messageData);
                break;

            case messageTypeEnum.DRAW_MARKER: {
                const drawTargetId = messageData.CamFrameInformation?.DeviceGUID;
                const shouldProcessDraw = this.store.freeze ||
                    !drawTargetId ||
                    drawTargetId === this.store.deviceId;

                if (shouldProcessDraw) {
                    this.drawingService.addDrawFromRemote(messageData, isSenderWeb);
                }
                break;
            }

            case messageTypeEnum.MARKER: {
                const markerTargetId = messageData.CamFrameInformation?.DeviceGUID;
                const shouldProcessMarker = this.store.freeze ||
                    !markerTargetId ||
                    markerTargetId === this.store.deviceId;

                if (shouldProcessMarker) {
                    this.drawingService.addMarkerFromRemote(messageData, isSenderWeb);
                }
                break;
            }
            case messageTypeEnum.COMMAND:
                this.handleDrawingCommand(messageData);
                break;

            case messageTypeEnum.MIC_STATE:
                this.participantService.handleMicState(messageData);
                break;

            case messageTypeEnum.CAMERA_FEED:
                this.participantService.handleCameraFeed(messageData);
                break;

            case messageTypeEnum.RECORDING_STARTED:
                this.recordingService.handleRecordingStarted().then();
                break;

            case messageTypeEnum.RECORDING_STOPPED:
                this.recordingService.handleRecordingStopped().then();
                break;

            case messageTypeEnum.RECORDER_HEARTBEAT:
                this.recordingService.resetTimeout('recorderHeartbeatTimeout', () => this.recordingService.autoStop(), RECORDING_STOP_THRESHOLD);
                break;

            case messageTypeEnum.RECORDER_TAKEOVER:
                this.socketManager.sendHeartbeat(this.store.roomId, this.store.deviceId, this.store.micOn, this.store.name, generalStore.userDetails).then();
                this.store.isNextFrameKey = true;
                break;

            default:
                if (![messageTypeEnum.REMOTE_PEER_SETTINGS, messageTypeEnum.HIGH_RES_FEED_SNAP, messageTypeEnum.TEXT].includes(messageData.MessageId)) {
                    console.log('unrecognized event', messageData);
                }
        }
    }

    /**
     * Handle chat message
     */
    handleChatMessage(content) {
        // This will be handled by existing chat logic in store
        // Just adding it to messages array
        this.store.newMessage = true;
        if (!this.store.isChatOpen) {
            this.store.unReadMessages++;
        }
        this.store.addReceivedMessage(content)
    }

    /**
     * Handle drawing command
     */
    handleDrawingCommand(content) {
        switch (content.Command) {
            case 'ClearLast':
                this.drawingService.clearLast(true);
                break;
            case 'Clear':
                this.drawingService.clearAll(true);
                break;
            case 'Freeze':
                // Only process freeze command if it's meant for this device
                if (!content.TargetDeviceId || content.TargetDeviceId === this.store.deviceId) {
                    this.store.freeze = !this.store.freeze;
                }
                break;
            default:
                console.log('unrecognized command', content);
        }
    }

    /**
     * Handle screen share started notification
     */
    handleShareScreenStarted(data) {
        const parsedData = JSON.parse(data);
        const participantId = parsedData.SenderSocketId;

        if (participantId === this.store.deviceId) {
            return;
        }

        // Stop own screen share if another participant starts
        if (this.store.shareScreenOn) {
            this.store.shareScreenOn = false;
        }
    }

    /**
     * Start heartbeat
     */
    startHeartbeat() {
        this.heartbeatInterval = setInterval(() => {
            this.socketManager.sendHeartbeat(this.store.roomId, this.store.deviceId, this.store.micOn, this.store.name, generalStore.userDetails);
        }, HEARTBEAT_INTERVAL);
    }

    /**
     * Stop heartbeat
     */
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }

    /**
     * Start ping interval
     */
    startPingInterval() {
        this.ping().then(); // Immediate ping
        this.pingInterval = setInterval(() => {
            this.ping();
        }, PING_INTERVAL);
    }

    /**
     * Stop ping interval
     */
    stopPingInterval() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    /**
     * Ping server
     */
    async ping() {
        try {
            const {participantsMap, newTranscriptionToken} = await RtcRequester.ping(
                this.store.roomId,
                this.store.password,
                this.store.deviceId,
                this.store.name,
                deviceTypeEnum.WEB,
                this.store.transcriptionToken
            );

            this.store.participants = participantsMap;

            if (newTranscriptionToken) {
                this.transcriptionService.updateToken(newTranscriptionToken);
            }
        } catch (error) {
            console.error('Ping error:', error);
        }
    }

    /**
     * Start play audio interval
     */
    startPlayAudioInterval() {
        this.playAudioInterval = setInterval(() => {
            this.streamService.playAudio();
        }, 50);
    }

    /**
     * Stop play audio interval
     */
    stopPlayAudioInterval() {
        if (this.playAudioInterval) {
            clearInterval(this.playAudioInterval);
            this.playAudioInterval = null;
        }
    }

    /**
     * Create session
     */
    async createSession() {
        this.store.sessionObject = await RtcRequester.createOrUpdateRoomSession({
            SupportSessionId: uuidv4(),
            RoomIdentifier: this.store.roomId,
            DeviceType: 'Web Browser',
            User: this.store.userDetails._id,
            StartTimeStamp: new Date(Date.now()).toISOString(),
            EndTimeStamp: null,
        });
    }

    /**
     * Close session
     */
    async closeSession() {
        if (this.store.sessionObject && this.store.sessionObject.SupportSessionId) {
            await RtcRequester.createOrUpdateRoomSession({
                ...this.store.sessionObject,
                EndTimeStamp: new Date(Date.now()).toISOString()
            });
            this.store.sessionObject = {};
        }
    }

    /**
     * Invite user to call
     */
    async inviteUser(invitedUserId) {
        await RtcRequester.inviteUser(this.store.roomId, this.store.password, invitedUserId);
    }

    /**
     * Get service instances (for direct access if needed)
     */
    getMediaService() {
        return this.mediaService;
    }

    getDrawingService() {
        return this.drawingService;
    }

    getStreamService() {
        return this.streamService;
    }

    getTranscriptionService() {
        return this.transcriptionService;
    }

    getParticipantService() {
        return this.participantService;
    }

    getSocketManager() {
        return this.socketManager;
    }
}
